import React from 'react';
import { BookOpen, Calculator, TrendingUp, PiggyBank, Shield, Home, DollarSign, Clock, Target, Briefcase, MapPin, Timer, CreditCard } from 'lucide-react';
import SEO from './SEO';
import { pageSEO } from '../seoData';

const Knowledge = () => {
  const guidelines = [
    {
      id: 1,
      title: "Rule of 72 - Double Your Money",
      icon: Calculator,
      color: "blue",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-900",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      formula: "72 ÷ Interest Rate = Years to Double Money",
      description: "Quickly calculate how long it takes to double your investment",
      examples: [
        "At 8% return: 72 ÷ 8 = 9 years to double",
        "At 12% return: 72 ÷ 12 = 6 years to double",
        "At 6% return: 72 ÷ 6 = 12 years to double"
      ],
      importance: "Helps you understand the power of compound interest and choose better investment options."
    },
    {
      id: 2,
      title: "Rule of 70 - Inflation Impact",
      icon: TrendingUp,
      color: "orange",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      textColor: "text-orange-900",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      formula: "70 ÷ Current Inflation Rate = Years for money to lose half its value",
      description: "Shows how inflation reduces your money's purchasing power",
      examples: [
        "At 7% inflation: Your ₹1 lakh will have purchasing power of only ₹50,000 in 10 years",
        "At 5% inflation: Money loses half value in 14 years"
      ],
      importance: "Understand why you must invest to beat inflation, not just save."
    },
    {
      id: 3,
      title: "Wealth Allocation Rule - Diversify Your Portfolio",
      icon: PiggyBank,
      color: "green",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      textColor: "text-green-900",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      formula: "Property (35%) + Equity/MF (35%) + Debt (15%) + Others (15%)",
      description: "Properly distribute your total wealth across different asset classes for optimal growth and risk management",
      examples: [
        "Property: 35% - Real estate investments",
        "Equity/Mutual Funds: 35% - Stock market investments",
        "FD/Liquid MF/Debt: 15% - Safe fixed income",
        "Bullion (Gold/Silver): 5% - Precious metals",
        "Tax Saving (PPF/Insurance): 5% - Tax-efficient instruments",
        "Foreign Investments: 5% - International diversification"
      ],
      importance: "Diversification reduces risk and ensures steady wealth growth across all market conditions."
    },
    {
      id: 4,
      title: "40% EMI Rule - Debt Management",
      icon: CreditCard,
      color: "purple",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      textColor: "text-purple-900",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      formula: "Total EMIs ≤ 40% of Monthly Income",
      description: "Keep your debt under control",
      examples: [
        "Monthly income: ₹50,000",
        "Maximum EMI limit: ₹20,000",
        "Safe borrowing capacity"
      ],
      importance: "Prevents over-borrowing and ensures you can comfortably repay loans while maintaining quality of life."
    },
    {
      id: 5,
      title: "Life Insurance Rule - Family Protection",
      icon: Shield,
      color: "yellow",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      textColor: "text-yellow-900",
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-600",
      formula: "Sum Assured = 20 × Annual Income",
      description: "Ensure adequate life insurance coverage",
      examples: [
        "Annual income: ₹5 lakhs",
        "Required insurance: ₹1 crore",
        "Protects family's financial future"
      ],
      importance: "Ensures your family can maintain their lifestyle even if something happens to you."
    },
    {
      id: 6,
      title: "Rule of 114 - Triple Your Money",
      icon: Target,
      color: "red",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-900",
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      formula: "114 ÷ Interest Rate = Years to Triple Money",
      description: "Calculate how long it takes to triple your investment",
      examples: [
        "At 12% return: 114 ÷ 12 = 9.5 years to triple",
        "At 15% return: 114 ÷ 15 = 7.6 years to triple"
      ],
      importance: "Helps set realistic expectations for long-term wealth tripling goals."
    },
    {
      id: 7,
      title: "15-15-15 Rule - SIP Magic",
      icon: Timer,
      color: "indigo",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200",
      textColor: "text-indigo-900",
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      formula: "₹15,000 monthly SIP for 15 years at 15% returns",
      description: "Shows the power of consistent SIP investing",
      examples: [
        "Total Investment: ₹27 lakhs",
        "Maturity Value: ₹1 crore+",
        "Returns: ₹73+ lakhs"
      ],
      importance: "Demonstrates how disciplined investing can create substantial wealth."
    },
    {
      id: 8,
      title: "Rule of 25 - Retirement Planning",
      icon: Clock,
      color: "gray",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
      textColor: "text-gray-900",
      iconBg: "bg-gray-100",
      iconColor: "text-gray-600",
      formula: "Annual Expenses × 25 = Required Retirement Corpus",
      description: "Calculate retirement corpus needed",
      examples: [
        "Annual expenses: ₹4 lakhs",
        "Required corpus: 4 × 25 = ₹1 crore",
        "Ensures lifelong financial security"
      ],
      importance: "Ensures you never run out of money in retirement."
    },
    {
      id: 9,
      title: "80-20 Rule - Investment Focus",
      icon: TrendingUp,
      color: "teal",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200",
      textColor: "text-teal-900",
      iconBg: "bg-teal-100",
      iconColor: "text-teal-600",
      formula: "80% of wealth comes from 20% of best investments",
      description: "Focus on investments that matter most",
      examples: [
        "Focus on 2-3 high-quality mutual funds rather than 10 average ones",
        "Concentrate on equity and real estate for wealth building",
        "Quality over quantity approach"
      ],
      importance: "Simplifies portfolio and maximizes returns through focused investing."
    },
    {
      id: 10,
      title: "6-Month Salary Rule - Job Security Fund",
      icon: Briefcase,
      color: "blue",
      bgColor: "bg-sky-50",
      borderColor: "border-sky-200",
      textColor: "text-sky-900",
      iconBg: "bg-sky-100",
      iconColor: "text-sky-600",
      formula: "Emergency Fund = 6 × Monthly Salary",
      description: "Build emergency fund for job transitions",
      examples: [
        "Monthly salary: ₹50,000",
        "Emergency fund needed: ₹3 lakhs",
        "Keep in liquid instruments"
      ],
      importance: "Provides financial security during career changes or job loss."
    },
    {
      id: 11,
      title: "1% Property Rule - Real Estate Investment",
      icon: Home,
      color: "amber",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-900",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      formula: "Monthly Rent = 1% of Property Purchase Price",
      description: "Evaluate rental property investments",
      examples: [
        "Property cost: ₹50 lakhs",
        "Expected monthly rent: ₹50,000",
        "Good rental yield indicator"
      ],
      importance: "Ensures your property investment generates decent rental yield."
    },
    {
      id: 12,
      title: "Rule of 5 - Investment Holding Period",
      icon: MapPin,
      color: "emerald",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      textColor: "text-emerald-900",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      formula: "Minimum 5 years holding period for equity investments",
      description: "Minimum holding period for equity investments",
      examples: [
        "Hold equity investments for minimum 5 years",
        "Reduces short-term volatility impact",
        "Maximizes long-term wealth creation"
      ],
      importance: "Allows compounding to work effectively and reduces market timing risks."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO {...pageSEO.knowledge} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="knowledge-title">
            📚 Financial Planning Services - Investment Knowledge Hub
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
            Essential Guidelines from Investment Advisor India Experts
          </p>
          <p className="text-lg text-gray-500 max-w-4xl mx-auto">
            Master these time-tested financial rules for wealth management India and retirement planning NRI. 
            Expert guidance from Kolkata's trusted mutual fund advisor and NRI investment consultant since 1996.
          </p>
        </div>

        {/* Guidelines Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {guidelines.map((guideline, index) => {
            const Icon = guideline.icon;
            return (
              <div
                key={guideline.id}
                className={`${guideline.bgColor} ${guideline.borderColor} border-2 rounded-xl p-6 hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
                data-testid={`guideline-${guideline.id}`}
              >
                {/* Header */}
                <div className="flex items-start mb-4">
                  <div className={`${guideline.iconBg} p-3 rounded-lg mr-4 flex-shrink-0`}>
                    <Icon className={`h-6 w-6 ${guideline.iconColor}`} />
                  </div>
                  <h3 className={`text-xl font-bold ${guideline.textColor} leading-tight`}>
                    {guideline.title}
                  </h3>
                </div>

                {/* Formula/Rule */}
                <div className={`bg-white p-3 rounded-lg border ${guideline.borderColor} mb-4`}>
                  <p className={`font-semibold ${guideline.textColor} text-sm`}>
                    <strong>Formula:</strong> {guideline.formula}
                  </p>
                </div>

                {/* Description */}
                <p className={`${guideline.textColor} mb-4 font-medium`}>
                  <strong>What it does:</strong> {guideline.description}
                </p>

                {/* Examples */}
                <div className="mb-4">
                  <p className={`font-semibold ${guideline.textColor} mb-2 text-sm`}>Examples:</p>
                  <ul className={`${guideline.textColor} text-sm space-y-1`}>
                    {guideline.examples.map((example, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className={`${guideline.textColor} mr-2`}>•</span>
                        <span>{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Importance */}
                <div className={`bg-white p-3 rounded-lg border ${guideline.borderColor}`}>
                  <p className={`text-sm ${guideline.textColor}`}>
                    <strong>Why it matters:</strong> {guideline.importance}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Apply These Rules?</h2>
          <p className="text-xl text-blue-100 mb-6 max-w-3xl mx-auto">
            These guidelines are the foundation of successful wealth creation. 
            Let our experts help you implement them in your financial journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/book-consultation'}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
            >
              Consult Our Advisor
            </button>
            <button
              onClick={() => window.location.href = '/calculator'}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-colors"
            >
              Calculate Your Numbers
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                <strong>💡 Remember:</strong> These are general guidelines based on established financial principles. 
                Your personal financial situation may require adjustments. Always consult with qualified Rumtek Finvest 
                advisors for personalized advice tailored to your specific goals, risk profile, and circumstances.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Knowledge;